import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  MessageSquare, Send, User, Loader2, Circle, 
  Search, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminChat = () => {
  const [conversations, setConversations] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const fetchDataRef = useRef(null);
  const fetchConversationRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchDataRef.current?.({ silent: true });
      if (selectedStudent?.id) {
        fetchConversationRef.current?.(selectedStudent.id, { silent: true });
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [selectedStudent?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const mergeConversation = (conversation, fallbackLastMessageAt = null) => {
    if (!conversation) return;

    setConversations(prev => {
      const nextConversation = {
        ...conversation,
        last_message_at: fallbackLastMessageAt || conversation.last_message_at,
      };
      const existingIndex = prev.findIndex(conv => conv.id === nextConversation.id);

      if (existingIndex === -1) {
        return [...prev, nextConversation];
      }

      return prev.map(conv => (
        conv.id === nextConversation.id
          ? { ...conv, ...nextConversation }
          : conv
      ));
    });
  };

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const [conversationsRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/conversations`),
        axios.get(`${API_URL}/api/admin/students?page_size=200`)
      ]);
      const nextStudents = (studentsRes.data.data ?? studentsRes.data).filter(s => s.status !== 'suspended');

      setConversations(conversationsRes.data);
      setStudents(nextStudents);
      setSelectedStudent(prev => (
        prev ? nextStudents.find(student => student.id === prev.id) || prev : prev
      ));
    } catch (err) {
      if (!silent) {
        console.error('Failed to fetch data:', err);
        toast.error('Failed to load conversations');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchConversation = async (studentId, { silent = false } = {}) => {
    if (!studentId) return;

    if (!silent) {
      setLoadingChat(true);
    }

    try {
      const response = await axios.get(`${API_URL}/api/admin/conversations/${studentId}`);
      const nextMessages = response.data.messages || [];
      const latestMessage = nextMessages[nextMessages.length - 1];

      setMessages(nextMessages);
      mergeConversation(response.data.conversation, latestMessage?.created_at);
    } catch (err) {
      if (!silent) {
        console.error('Failed to load chat:', err);
        toast.error(err.response?.data?.detail || 'Failed to load conversation');
      }
    } finally {
      if (!silent) {
        setLoadingChat(false);
      }
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    await fetchConversation(student.id);
  };

  fetchDataRef.current = fetchData;
  fetchConversationRef.current = fetchConversation;

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedStudent) return;

    setSending(true);
    try {
      await axios.post(`${API_URL}/api/admin/conversations/${selectedStudent.id}/messages`, {
        content: newMessage.trim()
      });

      await fetchConversation(selectedStudent.id, { silent: true });
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
  };

  const getUnreadCount = (studentId) => {
    const conv = conversations.find(c => c.student_id === studentId);
    return conv?.teacher_unread_count || 0;
  };

  const getLastMessage = (studentId) => {
    const conv = conversations.find(c => c.student_id === studentId);
    return conv?.last_message_at;
  };

  // Sort students: those with conversations first, then by last message
  const sortedStudents = [...students].sort((a, b) => {
    const aConv = conversations.find(c => c.student_id === a.id);
    const bConv = conversations.find(c => c.student_id === b.id);
    
    if (aConv && !bConv) return -1;
    if (!aConv && bConv) return 1;
    if (aConv && bConv) {
      return new Date(bConv.last_message_at) - new Date(aConv.last_message_at);
    }
    return a.name.localeCompare(b.name);
  });

  const filteredStudents = sortedStudents.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="admin-chat-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
          Student Chat
        </h1>
        <p className="text-[#1A2E6E]/60 mt-1">
          Direct messaging with students
        </p>
      </div>

      {/* Chat Container */}
      <div className="flex-1 bg-white rounded-xl border border-[#E8EAF0] overflow-hidden flex">
        {/* Students List */}
        <div className={`
          w-full lg:w-80 border-r border-[#E8EAF0] flex flex-col
          ${selectedStudent ? 'hidden lg:flex' : 'flex'}
        `}>
          {/* Search */}
          <div className="p-4 border-b border-[#E8EAF0]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A2E6E]/40" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="admin-chat-search-input"
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-[#E8EAF0] focus:border-[#1A2E6E] outline-none text-sm"
              />
            </div>
          </div>

          {/* Students */}
          <div className="flex-1 overflow-y-auto">
            {filteredStudents.length > 0 ? (
              filteredStudents.map(student => {
                const unread = getUnreadCount(student.id);
                const lastMessage = getLastMessage(student.id);
                
                return (
                  <button
                    key={student.id}
                    onClick={() => selectStudent(student)}
                    data-testid={`admin-chat-student-${student.id}`}
                    className={`
                      w-full p-4 flex items-center gap-3 hover:bg-[#FAFAF7] transition-colors border-b border-[#E8EAF0]
                      ${selectedStudent?.id === student.id ? 'bg-[#FAFAF7]' : ''}
                      ${unread > 0 ? 'bg-[#D72B2B]/5' : ''}
                    `}
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-[#1A2E6E]">
                          {student.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      {unread > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D72B2B] text-white text-xs rounded-full flex items-center justify-center">
                          {unread}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm truncate ${unread > 0 ? 'font-semibold text-[#1A2E6E]' : 'font-medium text-[#1A2E6E]'}`}>
                          {student.name}
                        </p>
                        {student.status === 'pending' && (
                          <span className="shrink-0 rounded-full bg-[#F5A623]/10 px-2 py-0.5 text-[10px] font-medium text-[#F5A623]">
                            Pending
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#1A2E6E]/50 truncate">
                        {lastMessage ? formatDate(lastMessage) : 'No messages'}
                      </p>
                    </div>
                    {unread > 0 && (
                      <Circle className="w-2 h-2 fill-[#D72B2B] text-[#D72B2B]" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="p-8 text-center">
                <User className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-3" />
                <p className="text-sm text-[#1A2E6E]/60">
                  {searchTerm ? 'No students found' : 'No students available'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`
          flex-1 flex flex-col
          ${selectedStudent ? 'flex' : 'hidden lg:flex'}
        `}>
          {selectedStudent ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-[#E8EAF0] flex items-center gap-3">
                <button
                  onClick={() => setSelectedStudent(null)}
                  className="lg:hidden p-2 -ml-2 text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="w-10 h-10 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-[#1A2E6E]">
                    {selectedStudent.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-[#1A2E6E]">{selectedStudent.name}</p>
                    {selectedStudent.status === 'pending' && (
                      <span className="rounded-full bg-[#F5A623]/10 px-2 py-0.5 text-[10px] font-medium text-[#F5A623]">
                        Pending approval
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#1A2E6E]/60">{selectedStudent.email}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingChat ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 text-[#D72B2B] animate-spin" />
                  </div>
                ) : messages.length > 0 ? (
                  messages.map((msg, index) => {
                    const isTeacher = msg.sender_role === 'teacher' || msg.sender_role === 'admin';
                    const showDate = index === 0 || 
                      formatDate(messages[index - 1].created_at) !== formatDate(msg.created_at);
                    
                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className="text-center">
                            <span className="text-xs text-[#1A2E6E]/50 bg-[#FAFAF7] px-3 py-1 rounded-full">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                        )}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${isTeacher ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`
                            max-w-[75%] rounded-2xl px-4 py-3
                            ${isTeacher 
                              ? 'bg-[#1A2E6E] text-white rounded-br-md' 
                              : 'bg-[#F3F4F6] text-[#1A2E6E] rounded-bl-md'}
                          `}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-1 ${isTeacher ? 'text-white/60' : 'text-[#1A2E6E]/50'}`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </motion.div>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-3" />
                      <p className="text-[#1A2E6E]/60">No messages yet</p>
                      <p className="text-sm text-[#1A2E6E]/40">Send a message to start the conversation</p>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="p-4 border-t border-[#E8EAF0]">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    data-testid="admin-chat-message-input"
                    className="flex-1 px-4 py-3 rounded-xl border border-[#E8EAF0] focus:border-[#1A2E6E] outline-none"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    data-testid="admin-chat-send-btn"
                    className="px-4 py-3 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-[#1A2E6E]/20 mx-auto mb-4" />
                <p className="text-[#1A2E6E]/60">Select a student to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChat;
